import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import reportsData from "../data/private/reports.json";
import flowGuidanceData from "../data/private/flowGuidance.json";
import { NavLink, useNavigate } from "react-router-dom";
import { logActivity, completeProgressStep, getProgress } from "../utils/activity";
import EmptyState from "../components/ui/EmptyState";
import ErrorState from "../components/ui/ErrorState";
import KeyValueTable from "../components/ui/KeyValueTable";
import LoadingState from "../components/ui/LoadingState";
import FlowGuidanceBanner from "../components/FlowGuidanceBanner";
import InfoBanner from "../components/InfoBanner";
import useModalBehavior from "../utils/useModalBehavior";
import normalizeApiError from "../utils/normalizeApiError";
import { usePlatformContext } from "../context/PlatformContext";
import { useClients } from "../context/ClientsContext";
import { useProperties } from "../context/PropertiesContext";
import { downloadReport, generateReport, getReport } from "../api/reports.api";
import { markSelectionUpdated, setDebugLastAction } from "../utils/debugState";
import {
  clearStoredProjectId,
  extractProjectIdFromProperty,
  persistProjectId,
} from "../utils/projectId";

export default function DashboardReports() {
  const navigate = useNavigate();
  const { user } = usePlatformContext();
  const [activeReport, setActiveReport] = useState(null);
  const { clients, error: clientsError } = useClients();
  const [selectedClientId, setSelectedClientId] = useState(
    localStorage.getItem("myv_selected_client_id") || ""
  );
  const [selectedPropertyId, setSelectedPropertyId] = useState(
    localStorage.getItem("myv_selected_property_id") || ""
  );
  const [selectedPropertyName, setSelectedPropertyName] = useState(
    localStorage.getItem("myv_selected_property") || ""
  );
  const [selectedModules, setSelectedModules] = useState([]);
  const { properties, error: propertiesError } = useProperties();
  const [loadError, setLoadError] = useState(null);
  const [reportItems, setReportItems] = useState([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportNotReady, setReportNotReady] = useState(false);
  const [reportResponse, setReportResponse] = useState(null);
  const [generationNotice, setGenerationNotice] = useState("");
  const [downloadNotice, setDownloadNotice] = useState("");
  const [analysisId, setAnalysisId] = useState(
    localStorage.getItem("myv_selected_analysis_id") || ""
  );
  const [coverInfo, setCoverInfo] = useState(() => ({
    consultantName: user?.name || "",
    clientName: localStorage.getItem("myv_selected_client") || "",
    propertyName: localStorage.getItem("myv_selected_property") || "",
  }));
  const [sections, setSections] = useState(() =>
    reportsData.preview.sections.reduce((acc, section) => {
      acc[section.key] = true;
      return acc;
    }, {})
  );
  const selectedClientName = useMemo(
    () => clients.find((client) => client.id === selectedClientId)?.data?.name || "",
    [clients, selectedClientId]
  );
  const selectedPropertyItem = useMemo(() => {
    if (selectedPropertyId) {
      return properties.find((property) => property.id === selectedPropertyId) || null;
    }
    if (selectedPropertyName) {
      return (
        properties.find((property) => property.data?.name === selectedPropertyName) ||
        null
      );
    }
    return null;
  }, [properties, selectedPropertyId, selectedPropertyName]);
  const modalRef = useRef(null);
  const handleClose = useCallback(() => {
    if (activeReport) {
      logActivity("report_preview_close", { id: activeReport.id || "" });
    }
    setActiveReport(null);
  }, [activeReport]);

  const progress = getProgress();
  useModalBehavior(Boolean(activeReport), handleClose, modalRef);

  useEffect(() => {
    if (!user) return;
    setCoverInfo((prev) => ({
      ...prev,
      consultantName: prev.consultantName || user?.name || "",
    }));
  }, [user]);

  useEffect(() => {
    if (!clientsError) return;
    setLoadError(normalizeApiError(clientsError));
  }, [clientsError]);

  useEffect(() => {
    if (!selectedClientName) return;
    setCoverInfo((prev) => ({
      ...prev,
      clientName: selectedClientName,
    }));
  }, [selectedClientName]);
  useEffect(() => {
    if (!selectedPropertyName) return;
    setCoverInfo((prev) => ({
      ...prev,
      propertyName: selectedPropertyName,
    }));
  }, [selectedPropertyName]);

  useEffect(() => {
    if (propertiesError) {
      setLoadError(normalizeApiError(propertiesError));
      return;
    }
    if (!clientsError) {
      setLoadError(null);
    }
  }, [clientsError, propertiesError]);

  useEffect(() => {
    if (!analysisId) {
      setReportItems([]);
      setReportResponse(null);
      setGenerationNotice("");
      setDownloadNotice("");
      return;
    }
    const fetchReports = async () => {
      setReportLoading(true);
      try {
        const data = await getReport(analysisId);
        const nextReport = data || null;
        setReportItems(nextReport ? [nextReport] : []);
        setReportResponse(nextReport);
        setGenerationNotice("");
        setReportNotReady(false);
      } catch (error) {
        if (error?.response?.status === 501) {
          setReportNotReady(true);
          setReportItems([]);
          setReportResponse(null);
          setGenerationNotice("");
          return;
        }
        setReportItems([]);
        setReportResponse(null);
        setGenerationNotice("");
        setLoadError(normalizeApiError(error));
      } finally {
        setReportLoading(false);
      }
    };
    fetchReports();
  }, [analysisId]);

  const missingClient = !selectedClientId;
  const missingProperty = !selectedPropertyId;
  const missingAnalysis = !analysisId;

  if (missingClient) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No client selected"
          message="Select a client to continue."
          testId="dashboard-empty-reports-client"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/clients")}>
          Select Client
        </button>
      </div>
    );
  }

  if (missingProperty) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No property selected"
          message="Select a property to continue."
          testId="dashboard-empty-reports-property"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/properties")}>
          Select Property
        </button>
      </div>
    );
  }

  if (missingAnalysis) {
    return (
      <div className="space-y-4">
        <EmptyState
          title="No analysis selected"
          message="Run analysis to continue."
          testId="dashboard-empty-reports-analysis"
        />
        <button className="btn btn-primary" onClick={() => navigate("/dashboard/analysis")}>
          Run Analysis
        </button>
      </div>
    );
  }
  const guidance = useMemo(() => {
    if (!selectedClientId) {
      return {
        key: "reports-missing-client",
        content: flowGuidanceData.reports.missingClient,
        meta: { page: "reports", missing: "client" },
      };
    }
    if (!selectedPropertyId && !selectedPropertyName) {
      return {
        key: "reports-missing-property",
        content: flowGuidanceData.reports.missingProperty,
        meta: { page: "reports", missing: "property" },
      };
    }
    if (!progress.analysis_viewed) {
      return {
        key: "reports-missing-analysis",
        content: flowGuidanceData.reports.missingAnalysis,
        meta: { page: "reports", missing: "analysis" },
      };
    }
    return null;
  }, [progress.analysis_viewed, selectedClientId, selectedPropertyId, selectedPropertyName]);

  const handleClientChange = (value) => {
    setSelectedClientId(value);
    localStorage.setItem("myv_selected_client_id", value);
    markSelectionUpdated("client");
    const match = clients.find((client) => client.id === value);
    const name = match?.data?.name || "";
    if (name) {
      localStorage.setItem("myv_selected_client", name);
    }
    setCoverInfo((prev) => ({
      ...prev,
      clientName: name || prev.clientName,
    }));
  };

  useEffect(() => {
    if (selectedClientId || clients.length === 0) return;
    const storedName = localStorage.getItem("myv_selected_client");
    if (!storedName) return;
    const match = clients.find((client) => client.data?.name === storedName);
    if (match?.id) {
      setSelectedClientId(match.id);
      localStorage.setItem("myv_selected_client_id", match.id);
      markSelectionUpdated("client");
    }
  }, [clients, selectedClientId]);

  const handlePropertyChange = (value) => {
    const match =
      properties.find((property) => property.id === value) ||
      properties.find((property) => property.data?.name === value) ||
      null;
    const nextId = match?.id || "";
    const nextName = match?.data?.name || value || "";
    const projectId = extractProjectIdFromProperty(match);
    if (projectId) {
      persistProjectId(projectId);
    } else {
      clearStoredProjectId();
    }
    setSelectedPropertyId(nextId);
    setSelectedPropertyName(nextName);
    if (nextId) {
      localStorage.setItem("myv_selected_property_id", nextId);
      markSelectionUpdated("property");
    }
    if (nextName) {
      localStorage.setItem("myv_selected_property", nextName);
    }
  };

  useEffect(() => {
    if (selectedPropertyId || !selectedPropertyName || properties.length === 0) return;
    const match = properties.find(
      (property) => property.data?.name === selectedPropertyName
    );
    if (match?.id) {
      setSelectedPropertyId(match.id);
      localStorage.setItem("myv_selected_property_id", match.id);
    }
  }, [properties, selectedPropertyId, selectedPropertyName]);


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">
          {reportsData.title}
        </h1>
        <p className="mt-2 text-sm text-slate-600">{reportsData.subtitle}</p>
      </div>
      {guidance && (
        <FlowGuidanceBanner
          eventKey={guidance.key}
          title={guidance.content.title}
          description={guidance.content.description}
          meta={guidance.meta}
        />
      )}
      {progress.report_previewed && reportsData.nextAction && (
        <div className="card p-4">
          <InfoBanner
            title={reportsData.nextAction.title}
            description={reportsData.nextAction.description}
          />
          <NavLink
            className="btn btn-secondary mt-3"
            to="/dashboard/prescriptions"
            onClick={() =>
              logActivity("prescription_opened", { source: "reports" })
            }
          >
            {reportsData.nextAction.ctaLabel}
          </NavLink>
        </div>
      )}

      <div className="card card-hover p-6">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <label className="text-xs text-slate-500">
              {reportsData.selectors.clientLabel}
            </label>
            <select
              className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              value={selectedClientId}
              onChange={(event) => handleClientChange(event.target.value)}
            >
              <option value="">{reportsData.selectors.clientLabel}</option>
              {clients.map((client) => (
                <option key={client.id || client.data?.name} value={client.id}>
                  {client.data?.name || ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">
              {reportsData.selectors.propertyLabel}
            </label>
            <select
              className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              value={selectedPropertyId || selectedPropertyName}
              onChange={(event) => handlePropertyChange(event.target.value)}
            >
              <option value="">{reportsData.selectors.propertyLabel}</option>
              {properties.map((property) => {
                const labelParts = [
                  property.data?.name,
                  property.data?.city,
                  property.data?.type,
                ].filter(Boolean);
                const label = labelParts.join(" • ");
                const optionValue = property.id || property.data?.name || "";
                return (
                  <option key={property.id || property.data?.name} value={optionValue}>
                    {label || property.data?.name || ""}
                  </option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Analysis ID</label>
            <input
              className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
              placeholder="Enter analysis ID"
              value={analysisId}
              onChange={(event) => {
                const value = event.target.value.trim();
                setAnalysisId(value);
                if (value) {
                  localStorage.setItem("myv_selected_analysis_id", value);
                  markSelectionUpdated("analysis");
                } else {
                  localStorage.removeItem("myv_selected_analysis_id");
                }
              }}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">
              {reportsData.selectors.modulesLabel}
            </label>
            <div className="mt-2 flex flex-wrap gap-2">
              {reportItems.map((report, index) => {
                const key =
                  report?.id || report?.analysisId || `report-${index}`;
                const label =
                  report?.status || report?.id || report?.analysisId || "Report";
                return (
                <label
                  key={key}
                  className="flex items-center gap-2 text-xs text-slate-600"
                >
                  <input
                    type="checkbox"
                    checked={selectedModules.includes(key)}
                    onChange={() =>
                      setSelectedModules((prev) =>
                        prev.includes(key)
                          ? prev.filter((item) => item !== key)
                          : [...prev, key]
                      )
                    }
                  />
                  {label}
                </label>
              )})}
            </div>
          </div>
        </div>
        {reportLoading && (
          <div className="mt-4">
            <LoadingState variant="table" rows={3} />
          </div>
        )}
        {reportResponse && (
          <div className="mt-4">
            <KeyValueTable data={reportResponse} title="Report response" />
          </div>
        )}
        {generationNotice && (
          <div className="mt-4 card p-4 text-xs text-slate-600">
            {generationNotice}
          </div>
        )}
        {selectedPropertyItem && (
          <div className="mt-4 grid gap-2 rounded-md border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 md:grid-cols-2">
            <p>Name: {selectedPropertyItem.data?.name || "—"}</p>
            <p>Type: {selectedPropertyItem.data?.type || "—"}</p>
            <p>City: {selectedPropertyItem.data?.city || "—"}</p>
            <p>State: {selectedPropertyItem.data?.state || "—"}</p>
            <p>Address: {selectedPropertyItem.data?.address || "—"}</p>
            <p>Pincode: {selectedPropertyItem.data?.pincode || "—"}</p>
            <p>Country: {selectedPropertyItem.data?.country || "—"}</p>
            <p>Client: {selectedPropertyItem.data?.client || "—"}</p>
            <p>Phone: {selectedPropertyItem.data?.phone || "—"}</p>
            <p>Email: {selectedPropertyItem.data?.email || "—"}</p>
            <p>Notes: {selectedPropertyItem.data?.notes || "—"}</p>
          </div>
        )}
        <button
          className="btn btn-primary mt-4"
          disabled={!analysisId || reportNotReady}
          title={
            reportNotReady
              ? "Report generation is not available yet."
              : !analysisId
              ? "Enter an analysis ID to generate a report."
              : "Generate report"
          }
          onClick={async () => {
            if (!analysisId) return;
            try {
              await generateReport(analysisId);
              setGenerationNotice("Report generation requested.");
              setDownloadNotice("");
              setLoadError(null);
              setDebugLastAction("Generate report");
            } catch (error) {
              if (error?.response?.status === 501) {
                setGenerationNotice("");
                setDownloadNotice("");
                setReportNotReady(true);
                setLoadError(null);
                return;
              }
              setGenerationNotice("");
              setDownloadNotice("");
              setLoadError(normalizeApiError(error));
            }
          }}
        >
          {reportsData.selectors.generateLabel}
        </button>
        {!analysisId && (
          <p className="mt-2 text-xs text-slate-500">
            Please enter an analysis ID to generate reports.
          </p>
        )}
        {reportNotReady && (
          <p className="mt-2 text-xs text-slate-500">
            Report generation is currently unavailable for this analysis.
          </p>
        )}
        {(selectedClientId ||
          selectedPropertyId ||
          selectedPropertyName ||
          selectedModules.length) && (
          <div className="mt-4 card p-4 text-sm text-slate-600">
            {reportsData.subtitle}
          </div>
        )}
        <div className="mt-4 flex items-center gap-2 text-xs text-slate-600">
          <span>{reportsData.confidence.label}</span>
          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs">
            {reportsData.confidence.level}
          </span>
          <span title={reportsData.confidence.tooltip} aria-label="Confidence info">
            ⓘ
          </span>
        </div>
      </div>

      {reportNotReady ? (
        <InfoBanner
          title="Reports coming soon"
          description="Report generation is not available yet."
        />
      ) : downloadNotice ? (
        <div className="card p-4 text-sm text-slate-600">
          {downloadNotice}
        </div>
      ) : reportItems.length === 0 ? (
        <EmptyState
          title="No reports generated yet"
          message="Generate a report to see it here."
          testId="dashboard-empty-reports"
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {reportItems.map((report, index) => {
            const title = report?.id || report?.analysisId || "Report";
            const description =
              report?.status ||
              (typeof report === "string" ? report : "");
            return (
            <div key={report?.id || title || index} className="card card-hover p-4">
              <p className="text-sm font-semibold text-slate-900">
                {title}
              </p>
              <p className="mt-2 text-sm text-slate-600">
                {description}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setActiveReport(report);
                    setCoverInfo((prev) => ({
                      ...prev,
                      clientName: selectedClientName || prev.clientName,
                      propertyName: selectedPropertyName || prev.propertyName,
                    }));
                    logActivity("report_preview_open", { id: title });
                    logActivity("report_previewed", { id: title });
                    completeProgressStep("report_previewed");
                  }}
                >
                  {reportsData.previewLabel}
                </button>
                <button
                  className="btn btn-primary"
                  title={
                    !analysisId
                      ? "Enter an analysis ID to download a report."
                      : reportsData.downloadTooltip
                  }
                  disabled={!analysisId}
                  onClick={async () => {
                    if (!analysisId) return;
                    try {
                      const data = await downloadReport(analysisId);
                      setReportResponse(data || null);
                      setDownloadNotice("Report download ready.");
                      setLoadError(null);
                      setDebugLastAction("Download report");
                    } catch (error) {
                      if (error?.response?.status === 501) {
                        setReportNotReady(true);
                        setDownloadNotice("");
                        setLoadError(null);
                        return;
                      }
                      setReportResponse(null);
                      setDownloadNotice("");
                      setLoadError(normalizeApiError(error));
                    }
                  }}
                >
                  {reportsData.downloadLabel}
                </button>
              </div>
            </div>
          )})}
        </div>
      )}

      <AnimatePresence>
        {activeReport && (
          <motion.div
            className="modal-backdrop fixed inset-0 z-40 flex items-center justify-center px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          >
            <motion.div
              ref={modalRef}
              className="card modal-panel w-full max-w-lg p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="dashboard-report-preview-title"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              onClick={(event) => event.stopPropagation()}
            >
              <h2
                id="dashboard-report-preview-title"
                className="text-lg font-semibold text-slate-900"
              >
                {activeReport.id || activeReport.analysisId || "Report"}
              </h2>
              <p className="mt-2 text-sm text-slate-600">
                {activeReport.status ||
                  (typeof activeReport === "string" ? activeReport : "")}
              </p>
              <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                <p className="text-sm font-semibold text-slate-900">
                  {reportsData.preview.title}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {reportsData.preview.subtitle}
                </p>
              </div>
              <div className="mt-4 grid gap-4 md:grid-cols-[1fr_1fr]">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {reportsData.preview.coverTitle}
                    </p>
                    <div className="mt-3 grid gap-3">
                      {reportsData.preview.coverFields.map((field) => (
                        <div key={field.key}>
                          <label className="text-xs text-slate-500">
                            {field.label}
                          </label>
                          <input
                            className="mt-2 w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                            placeholder={field.placeholder}
                            value={coverInfo[field.key]}
                            onChange={(event) =>
                              setCoverInfo((prev) => ({
                                ...prev,
                                [field.key]: event.target.value,
                              }))
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      {reportsData.preview.sectionsTitle}
                    </p>
                    <div className="mt-3 space-y-2">
                      {reportsData.preview.sections.map((section) => {
                        const included = Boolean(sections[section.key]);
                        return (
                          <div
                            key={section.key}
                            className="flex items-center justify-between text-sm text-slate-600"
                          >
                            <span>{section.label}</span>
                            <button
                              className={`rounded-full px-3 py-1 text-xs ${
                                included
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                              onClick={() => {
                                setSections((prev) => {
                                  const next = {
                                    ...prev,
                                    [section.key]: !prev[section.key],
                                  };
                                  logActivity("report_section_toggled", {
                                    section: section.key,
                                    included: next[section.key],
                                  });
                                  return next;
                                });
                              }}
                              type="button"
                            >
                              {included
                                ? reportsData.preview.includeLabel
                                : reportsData.preview.excludeLabel}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {reportsData.preview.previewTitle}
                  </p>
                  <div className="mt-3 h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                    <p className="text-xs uppercase text-slate-400">
                      {activeReport.id ||
                        activeReport.analysisId ||
                        "Report"}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-slate-900">
                      {coverInfo.consultantName ||
                        reportsData.preview.coverTitle}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      {coverInfo.clientName ||
                        reportsData.selectors.clientLabel}
                    </p>
                    <p className="text-xs text-slate-500">
                      {coverInfo.propertyName ||
                        reportsData.selectors.propertyLabel}
                    </p>
                    <div className="mt-4 space-y-2">
                      {reportsData.preview.sections
                        .filter((section) => sections[section.key])
                        .map((section) => (
                          <div
                            key={section.key}
                            className="rounded-md border border-slate-200 bg-white p-3"
                          >
                            <p className="text-xs font-semibold text-slate-700">
                              {section.label}
                            </p>
                            <p className="mt-1 text-xs text-slate-500">
                              {reportsData.subtitle}
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
              <button
                className="btn btn-secondary mt-4 w-full"
                onClick={handleClose}
              >
                {reportsData.previewLabel}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {loadError && <ErrorState error={loadError} />}
    </div>
  );
}
